import { useTranslatedTag, useTranslation, useUserInfo, useUserSettings } from "@next-platform/client-common-core";
import {
  CharacterCard as CharacterCardV2,
  CharacterCardActions,
  CharacterCardDisplayMode,
  CharacterCardOrientation,
  CharacterCardVariant,
  DropDownMenuItem,
  IconVariants,
} from "@next-platform/client-common-spicy-ui";
import { Lorebook } from "@next-platform/packages-ts-types";

import { getCharacterCardStatusBadgeLabel } from "@/utils";
import { getAvatarUrl } from "@/utils/getAvatarUrl";
import getLorebookVariant from "@/utils/getLorebookVariant";

const DEFAULT_HIDDEN_ACTIONS_STATE = {
  [CharacterCardActions.SHARE]: { isHidden: true },
  [CharacterCardActions.BLOCK]: { isHidden: true },
  [CharacterCardActions.REPORT]: { isHidden: true },
  [CharacterCardActions.FAVORITE]: { isHidden: true },
  [CharacterCardActions.INFO]: { isHidden: true },
};

type CharacterCardActionsState = Partial<Record<CharacterCardActions, { isHidden?: boolean; isDisabled?: boolean; isLiked?: boolean }>>;

const getIsAvatarBlurred = ({
  isAuthenticated,
  blurNSFW,
  avatarIsNsfw,
}: {
  isAuthenticated: boolean;
  blurNSFW: boolean;
  avatarIsNsfw?: boolean;
}): boolean => (!isAuthenticated || blurNSFW) && !!avatarIsNsfw;

interface ILorebookCardProps {
  lorebook: Lorebook;
  navigate?: (path: string) => void;
  onPressDelete?: (lorebook: Lorebook) => void;
  onPressClone?: (lorebook: Lorebook) => void;
  dropdownMenuActions?: DropDownMenuItem[];
  /** Optional handler for card actions (e.g. FAVORITE) wired up by the discover feed. Falls back to no-op. */
  characterCardActionsHandler?: (action: CharacterCardActions, characterId: string) => void;
  /** Optional state describing each card action (visibility, liked state). Falls back to all hidden. */
  characterCardActionsState?: CharacterCardActionsState;
}

const LorebookCard: React.FC<ILorebookCardProps> = ({
  lorebook,
  navigate,
  onPressDelete,
  onPressClone,
  dropdownMenuActions,
  characterCardActionsHandler,
  characterCardActionsState,
}) => {
  const { t } = useTranslation();
  const { getTranslatedTag } = useTranslatedTag();
  const { isAuthenticated } = useUserInfo();
  const { blurNSFW } = useUserSettings();
  const avatarUrl = getAvatarUrl(lorebook?.avatar_url ?? "", { returnWithOriginalRatio: true }) as string;
  const tags = lorebook?.tags?.map((tag) => getTranslatedTag(tag)) || [];
  const variant = getLorebookVariant(lorebook);
  const isAvatarBlurred = getIsAvatarBlurred({ isAuthenticated, blurNSFW, avatarIsNsfw: lorebook?.avatar_is_nsfw });

  const statusBadgeLabel = getCharacterCardStatusBadgeLabel(variant, t);
  const lorebookDropdownMenuActions = [...(dropdownMenuActions || [])];

  if (onPressDelete || onPressClone) {
    lorebookDropdownMenuActions.push(
      ...[
        {
          label: t("myCreation:card.actions.edit"),
          icon: IconVariants.EDIT,
          onPress: () => {
            navigate?.(`/lorebook/edit/${lorebook.id}`);
          },
        },
      ],
    );
  }

  if (onPressDelete) {
    lorebookDropdownMenuActions.push({
      label: t("myCreation:card.actions.remove"),
      icon: IconVariants.TRASH,
      onPress: () => {
        onPressDelete(lorebook);
      },
      className: "text-red-9",
    });
  }

  if (onPressClone) {
    lorebookDropdownMenuActions.push({
      label: t("myCreation:card.actions.clone"),
      icon: IconVariants.COPY,
      isDisabled:
        variant === CharacterCardVariant.NEEDS_REVIEW ||
        variant === CharacterCardVariant.REVIEW ||
        variant === CharacterCardVariant.UNDER_MANUAL_REVIEW ||
        variant === CharacterCardVariant.ACTION,
      onPress: () => {
        onPressClone(lorebook);
      },
    });
  }

  const characterInfo = {
    chatPagePath: `/lorebook/${lorebook.id}`,
    chatbotProfilePagePath: `/lorebook/${lorebook.id}`,
    creatorPagePath: `/creator/${lorebook.creator_username}`,
    characterId: lorebook.id,
    characterName: lorebook.name,
    characterCreatorUserName: lorebook.creator_username || "",
    characterTitle: lorebook.description,
    characterMessageNumber: 0,
    entriesCount: lorebook?.num_entries || 0,
    characterAvatarUrl: avatarUrl,
    characterAvatarBlur: isAvatarBlurred,
    characterTags: tags,
    statusBadgeLabel,
  };

  const resolvedActionsState = characterCardActionsState ?? DEFAULT_HIDDEN_ACTIONS_STATE;

  return (
    <CharacterCardV2
      key={lorebook.id}
      orientation={CharacterCardOrientation.HORIZONTAL}
      dropdownMenuActions={lorebookDropdownMenuActions}
      characterInfo={characterInfo}
      characterCardActionsState={resolvedActionsState}
      characterCardActionsHandler={characterCardActionsHandler}
      variant={variant}
      displayMode={CharacterCardDisplayMode.STATUS}
    />
  );
};

export default LorebookCard;
